import { useAuth0 } from "@auth0/auth0-react";
import { Badge, Box, Typography } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { Menu, Sidebar, useProSidebar } from "react-pro-sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import { POCContext } from "../../contexts/POCContext";
import SvgToIcon from "../../icons/SvgToIcon";
import InboxDrawer from "../../scenes/settings/inbox/InboxDrawer";
import { sidebarIcons } from "../../scenes/stack/environment/icons/iconExport";
import POCTag from "../poc/POCTag";
import { SidebarMenuAccountItem } from "./SidebarMenuAccountItem";
import { SidebarMenuItem } from "./SidebarMenuItem";
import { SidebarMenuSubItem } from "./SidebarMenuSubItem";

const AppSidebar = ({ items }) => {
  const colors = useColors();
  const { collapseSidebar, collapsed } = useProSidebar();
  const [inboxDrawerOpen, setOpenInboxDrawer] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const location = useLocation();
  const { isPOC } = useContext(POCContext);
  const { getPOCLimits } = useAPI();
  const [pocLimits, setPOCLimits] = useState({});
  const { user } = useAuth0();
  const navigate = useNavigate();
  const [hasOverflow, setHasOverflow] = useState(false);
  const menuRef = useRef(null);
  const account = items.find((item) => item.name === "Account");

  useEffect(() => {
    if (isPOC) {
      getPOCLimits().then((response) => {
        setPOCLimits(response.data);
      });
    }
  }, [isPOC, location]);

  useEffect(() => {
    const checkOverflow = () => {
      if (menuRef.current) {
        setHasOverflow(
          menuRef.current.scrollHeight > menuRef.current.clientHeight
        );
      }
    };

    const observer = new MutationObserver(checkOverflow);
    if (menuRef.current) {
      observer.observe(menuRef.current, { childList: true, subtree: true });
    }

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      if (menuRef.current) {
        observer.disconnect();
      }
    };
  }, [items, collapsed]);
  const onboarding = location.pathname.includes("onboarding");

  return (
    <>
      {items.length !== 0 && (
        <Box
          display="flex"
          zIndex={onboarding ? 2000 : 0}
          sx={{
            borderRight: "1px solid " + colors.divider2,
            boxShadow: "1px 0px 24px 10px " + colors.sidebar_box_shadow,
            backgroundColor: colors.grey_50,
            "& .ps-menu-button:hover": {
              backgroundColor: colors.grey_50 + " !important",
            },
          }}
        >
          <Sidebar
            defaultCollapsed={true}
            rootStyles={{
              border: "none",
            }}
            width="230px"
            collapsedWidth={hasOverflow ? "95px" : "90px"}
            backgroundColor={colors.grey_50}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
                backgroundColor: colors.grey_50,
                gap: 2,
              }}
              ref={menuRef}
            >
              <Box>
                {/* Header */}
                <Box
                  style={{
                    position: "sticky",
                    top: "0",
                    zIndex: "1000",
                    backgroundColor: colors.grey_50,
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      onClick={() => {
                        if (user.email.includes("breeze")) {
                          navigate("/settings/support");
                        } else navigate("/");
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: collapsed ? "center" : "flex-start",
                        width: collapsed
                          ? hasOverflow
                            ? "60px"
                            : "40px"
                          : "100%",
                        maxWidth: collapsed
                          ? hasOverflow
                            ? "60px"
                            : "40px"
                          : "100%",
                        boxSizing: "border-box",
                        pt: "16px",
                        pb: "16px",
                        cursor: "pointer",
                      }}
                    >
                      <SvgToIcon
                        IconLogo={sidebarIcons.LogoCollapsed}
                        style={{
                          width: "30px",
                          height: "32px",
                        }}
                      />
                    </Box>
                    {isPOC && <POCTag pocLimits={pocLimits} />}
                    {/* collapse/Expand Icon */}
                  </Box>
                </Box>
                <Box>
                  <Menu>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 2,
                        height: "100%",
                        backgroundColor: colors.grey_50,
                      }}
                    >
                      {/* MENU ITEMS */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {items.map((item, index) =>
                          item.pages ? (
                            <SidebarMenuSubItem
                              collapsed={collapsed}
                              key={item.name}
                              title={item.name}
                              pages={item.pages}
                              theLast={index === items.length - 2}
                            />
                          ) : item.name === "Account" ? (
                            <></>
                          ) : (
                            <SidebarMenuItem
                              key={item.name}
                              title={item.name}
                              path={item.path}
                              icon={item.icon}
                              collapsed={collapsed}
                              notPermitted={item.notPermitted}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  </Menu>
                </Box>
              </Box>

              <Box
                style={{
                  position: "sticky",
                  bottom: "0",
                  zIndex: "1000",
                  backgroundColor: colors.grey_50,
                  padding: "16px 0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {/* Inbox button */}
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "1px",
                    padding: "4px",
                    borderRadius: "8px",
                    width: "90px",
                  }}
                  onClick={() => {
                    setOpenInboxDrawer(!inboxDrawerOpen);
                  }}
                >
                  <Badge
                    badgeContent={unreadNotifications}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.5rem",
                        minWidth: "14px",
                        height: "14px",
                        top: "3px",
                        right: "3px",
                      },
                    }}
                  >
                    <SvgToIcon
                      IconLogo={sidebarIcons.InboxIcon}
                      style={{
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </Badge>

                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "10px",
                    }}
                  >
                    Inbox
                  </Typography>
                </Box>
                {/* Account button*/}
                {account && (
                  <SidebarMenuAccountItem
                    key={account.name}
                    title={account.name}
                    path={account.path}
                    icon={account.icon}
                    collapsed={collapsed}
                  />
                )}
              </Box>
            </Box>
          </Sidebar>
        </Box>
      )}
      <InboxDrawer
        inboxDrawerOpen={inboxDrawerOpen}
        setOpenInboxDrawer={setOpenInboxDrawer}
        setUnreadNotifications={setUnreadNotifications}
      />
    </>
  );
};

export default AppSidebar;
